import { supabaseClient } from "@/lib/supabase";
import type { Template } from "@patient-watch/types";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../utils/auth";

export const selectTemplates = async (userId: string, isAdmin?: boolean) => {
    // Guard against invalid userId (unless admin fetching all)
    if (!userId && !isAdmin) {
        return [];
    }

    const selectColumns = isAdmin
        ? "*, templates_access(*), owner_profile:profiles!templates_user_id_fkey(name)"
        : "*, templates_access(*)";

    const query = supabaseClient.from("templates").select(selectColumns).order("updated_at", { ascending: false });

    // Admins see all templates; others only their own
    if (!isAdmin && userId) {
        query.eq("user_id", userId);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Ensure we always return an array and filter out any null values
    if (!data) return [];
    return (data as unknown as Template["Row"][]).filter(Boolean);
};

export const templatesQueryKey = "templates";

export const templateQueryOptions = (userId: string, isAdmin?: boolean) => {
    return {
        queryKey: [templatesQueryKey, userId, isAdmin],
        queryFn: () => selectTemplates(userId, isAdmin),
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    };
};

export const useTemplates = () => {
    const auth = useAuth();
    const isAdmin = auth?.roles?.isAdmin || auth?.isSuperUser;
    const result = useQuery(templateQueryOptions(auth?.id ?? "", isAdmin));

    // Ensure we always return an array, even if data is undefined or null
    return {
        ...result,
        data: result.data ?? [],
    };
};

export const selectTemplateById = async (userId: string, templateId: string) => {
    const { data, error } = await supabaseClient
        .from("templates")
        .select("*, templates_access(*)")
        .eq("user_id", userId)
        .eq("template_id", templateId)
        .single();

    if (error) throw error;
    return data as Template["Row"];
};

export const templatesByUserIdAndTemplateIdQueryOptions = (userId: string, templateId: string) => {
    return {
        queryKey: [templatesQueryKey, userId, templateId],
        queryFn: () => selectTemplateById(userId, templateId),
    };
};
